<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  buildThemeStyle,
  resolveHeaderImageUrl,
  resolveLogoUrl,
} from 'forms/helpers/theme';
import { useFormSteps } from 'forms/helpers/useFormSteps';

// Non-interactive mirror of the hosted form page (app/javascript/forms/App.vue
// + FormField.vue): same field-by-type rendering, same theme CSS variables,
// so what the operator sees here is what a visitor sees at /f/:slug. Inputs
// are shown but disabled — this pane never submits anything.

const props = defineProps({
  name: { type: String, default: '' },
  fields: { type: Array, default: () => [] },
  theme: { type: Object, default: () => ({}) },
  consentEnabled: { type: Boolean, default: false },
  consentText: { type: String, default: '' },
});

const { t } = useI18n();

const themeStyle = computed(() => buildThemeStyle(props.theme));
const logoUrl = computed(() => resolveLogoUrl(props.theme));
const headerImageUrl = computed(() => resolveHeaderImageUrl(props.theme));
// Imagem de topo = hero da boas-vindas; só entra no formulário se header_on_form.
const formHeaderImageUrl = computed(() =>
  props.theme?.header_on_form ? headerImageUrl.value : ''
);

// Optional welcome/cover screen (theme.welcome) — mirrors the hosted page's
// app/javascript/forms/App.vue welcome step. The preview starts on it
// whenever it's enabled (so the operator sees what a visitor sees first),
// but "Iniciar"/click lets them skip ahead to the fields, and a small
// toggle brings it back without leaving the tab.
const welcome = computed(() => props.theme?.welcome || {});
const previewShowingWelcome = ref(!!welcome.value.enabled);
watch(
  () => welcome.value.enabled,
  enabled => {
    previewShowingWelcome.value = !!enabled;
  }
);

const hasInlineConsentField = computed(() =>
  props.fields.some(fieldItem => fieldItem.type === 'consent')
);
const showStandaloneConsent = computed(
  () => props.consentEnabled && !hasInlineConsentField.value
);

const normalizedOptions = fieldItem =>
  (fieldItem.options || []).map(option => {
    if (option && typeof option === 'object') {
      return {
        value: option.value ?? option.label ?? '',
        label: option.label ?? option.value ?? '',
      };
    }
    return { value: option, label: option };
  });

const nativeInputType = fieldItem => {
  // Tipos que não existem como input nativo caem em texto: sem isso a prévia
  // renderiza <input type="scheduling"> e o navegador engole o atributo.
  const map = {
    phone: 'tel',
    rating: 'text',
    scheduling: 'text',
    cpf: 'text',
    cnpj: 'text',
    cep: 'text',
  };
  return map[fieldItem.type] || fieldItem.type;
};

// Mirrors App.vue's stepHelperText()/bareField(): the steps layout shows the
// field's own label as a big on-screen question, so the description (or the
// placeholder when there is no description) becomes the helper line — and in
// that fallback case the placeholder is not also duplicated inside the input.
const helperText = fieldItem =>
  fieldItem.description || fieldItem.placeholder || '';
const barePlaceholder = fieldItem =>
  fieldItem.description ? fieldItem.placeholder : '';

// Same layout choice, same helper, as the hosted page (app/javascript/forms/
// App.vue) — this mirrors the real step behavior instead of a static mockup.
const isStepsLayout = computed(() => props.theme?.layout === 'steps');
const fieldsRef = computed(() => props.fields);
const {
  currentIndex: stepIndex,
  totalSteps,
  currentStepFields,
  isFirstStep,
  isLastStep,
  progress,
  goNext,
  goPrev,
  reset: resetSteps,
} = useFormSteps({ fields: fieldsRef });

// Which fields the card currently renders: all of them (single layout) or
// just the active step's (steps layout). Keeping a single field-rendering
// block that iterates this list — instead of duplicating the per-field
// markup for each layout — is what keeps the preview from drifting.
const visibleFields = computed(() =>
  isStepsLayout.value ? currentStepFields.value : fieldsRef.value
);

// Restart the preview at step 1 when the layout toggle changes, so switching
// modes always shows a clean first impression rather than a stale index.
watch(isStepsLayout, () => resetSteps());
</script>

<template>
  <div
    class="flex flex-col rounded-2xl overflow-hidden border border-n-weak shadow-sm"
  >
    <div
      class="flex items-center gap-1.5 px-3 py-2 bg-n-solid-2 border-b border-n-weak"
      aria-hidden="true"
    >
      <span class="w-2.5 h-2.5 rounded-full bg-n-ruby-9 opacity-50" />
      <span class="w-2.5 h-2.5 rounded-full bg-n-amber-9 opacity-50" />
      <span class="w-2.5 h-2.5 rounded-full bg-n-teal-9 opacity-50" />
      <span
        class="ml-2 px-2.5 py-0.5 rounded-full bg-n-solid-1 text-n-slate-10 text-[11px] font-medium tracking-wide"
      >
        {{ t('FORMS_MGMT.EDITOR.PREVIEW.BROWSER_LABEL') }}
      </span>
    </div>

    <button
      v-if="welcome.enabled && !previewShowingWelcome"
      type="button"
      class="csfp-welcome-toggle"
      @click="previewShowingWelcome = true"
    >
      <span class="i-lucide-rotate-ccw" aria-hidden="true" />
      {{ t('FORMS_MGMT.EDITOR.PREVIEW.WELCOME_TOGGLE') }}
    </button>

    <div class="csfp-viewport" :style="themeStyle">
      <div v-if="!fields.length" class="csfp-empty">
        <span class="i-lucide-layout-template csfp-empty__icon" />
        <p>{{ t('FORMS_MGMT.EDITOR.PREVIEW.EMPTY') }}</p>
      </div>

      <!-- Welcome/cover screen (theme.welcome): full-bleed, mirrors the
           hosted page's app/javascript/forms/App.vue welcome step. -->
      <div
        v-else-if="welcome.enabled && previewShowingWelcome"
        class="csfp-welcome"
      >
        <img v-if="logoUrl" :src="logoUrl" alt="" class="csfp-welcome__logo" />
        <img
          v-if="headerImageUrl"
          :src="headerImageUrl"
          alt=""
          class="csfp-welcome__cover"
        />
        <p class="csfp-welcome__title">
          {{ welcome.title || name || t('FORMS_MGMT.EDITOR.PREVIEW.UNTITLED') }}
        </p>
        <p v-if="welcome.description" class="csfp-welcome__desc">
          {{ welcome.description }}
        </p>
        <button
          type="button"
          class="csfp-welcome__button"
          @click="previewShowingWelcome = false"
        >
          {{
            welcome.button_text ||
            t('FORMS_MGMT.EDITOR.WELCOME.BUTTON_PLACEHOLDER')
          }}
          <span class="i-lucide-arrow-right" aria-hidden="true" />
        </button>
      </div>

      <!-- Steps (Typeform-style) layout: full-bleed, no card — mirrors the
           hosted page's app/javascript/forms/App.vue steps rendering. -->
      <div v-else-if="isStepsLayout" class="csfp-flow">
        <div
          v-if="totalSteps > 1"
          class="csfp-progress"
          role="progressbar"
          :aria-valuenow="stepIndex + 1"
          :aria-valuemin="1"
          :aria-valuemax="totalSteps"
        >
          <span
            class="csfp-progress__fill"
            :style="{ width: `${progress * 100}%` }"
          />
        </div>

        <div class="csfp-flow__header">
          <img v-if="logoUrl" :src="logoUrl" alt="" class="csfp-flow__logo" />
          <span v-if="totalSteps > 1" class="csfp-flow__count">
            {{
              t('FORMS_MGMT.EDITOR.PREVIEW.STEP_PROGRESS', {
                current: stepIndex + 1,
                total: totalSteps,
              })
            }}
          </span>
        </div>

        <div class="csfp-flow__center">
          <img
            v-if="formHeaderImageUrl && isFirstStep"
            :src="formHeaderImageUrl"
            alt=""
            class="csfp-flow__cover"
          />

          <!-- One-field-per-step is the common case (fieldIdx is always 0
               then), rendered exactly as before. A step with MULTIPLE
               fields — because a later field opted into
               group_with_previous — keeps the big-question treatment for
               the first field only; the rest stack below as smaller
               sub-fields (.csfp-qmore), mirroring App.vue's .csf-qmore. -->
          <template
            v-for="(fieldItem, fieldIdx) in visibleFields"
            :key="fieldItem.key"
          >
            <div
              v-if="fieldItem.type !== 'consent' && fieldIdx === 0"
              class="csfp-field csfp-qhead"
            >
              <span class="csfp-qnum" aria-hidden="true">
                {{ stepIndex + 1 }}
                <span class="i-lucide-corner-down-right" />
              </span>
              <div class="csfp-qbody">
                <p class="csfp-qtitle" :data-required="fieldItem.required">
                  {{
                    fieldItem.label ||
                    t('FORMS_MGMT.EDITOR.PREVIEW.UNTITLED_FIELD')
                  }}
                </p>
                <p v-if="helperText(fieldItem)" class="csfp-qhelp">
                  {{ helperText(fieldItem) }}
                </p>

                <textarea
                  v-if="fieldItem.type === 'textarea'"
                  class="csfp-input csfp-textarea csfp-input--bare"
                  rows="3"
                  disabled
                  :placeholder="barePlaceholder(fieldItem)"
                />

                <select
                  v-else-if="fieldItem.type === 'select'"
                  class="csfp-input csfp-select csfp-input--bare"
                  disabled
                >
                  <option>
                    {{
                      fieldItem.placeholder ||
                      t('FORMS_MGMT.EDITOR.PREVIEW.SELECT_PLACEHOLDER')
                    }}
                  </option>
                </select>

                <div
                  v-else-if="fieldItem.type === 'radio'"
                  class="csfp-choices csfp-choices--bare"
                >
                  <label
                    v-for="option in normalizedOptions(fieldItem)"
                    :key="option.value"
                    class="csfp-choice csfp-choice--bare"
                  >
                    <input type="radio" disabled />
                    <span>{{ option.label }}</span>
                  </label>
                </div>

                <div
                  v-else-if="fieldItem.type === 'checkbox'"
                  class="csfp-choices csfp-choices--bare"
                >
                  <label
                    v-for="option in normalizedOptions(fieldItem)"
                    :key="option.value"
                    class="csfp-choice csfp-choice--bare"
                  >
                    <input type="checkbox" disabled />
                    <span>{{ option.label }}</span>
                  </label>
                </div>

                <input
                  v-else
                  class="csfp-input csfp-input--bare"
                  :type="nativeInputType(fieldItem)"
                  :placeholder="barePlaceholder(fieldItem)"
                  disabled
                />
              </div>
            </div>

            <div
              v-else-if="fieldItem.type !== 'consent'"
              class="csfp-field csfp-qmore"
            >
              <p class="csfp-qmore__label" :data-required="fieldItem.required">
                {{
                  fieldItem.label ||
                  t('FORMS_MGMT.EDITOR.PREVIEW.UNTITLED_FIELD')
                }}
              </p>
              <p v-if="helperText(fieldItem)" class="csfp-qmore__help">
                {{ helperText(fieldItem) }}
              </p>

              <textarea
                v-if="fieldItem.type === 'textarea'"
                class="csfp-input csfp-textarea csfp-input--bare"
                rows="3"
                disabled
                :placeholder="barePlaceholder(fieldItem)"
              />

              <select
                v-else-if="fieldItem.type === 'select'"
                class="csfp-input csfp-select csfp-input--bare"
                disabled
              >
                <option>
                  {{
                    fieldItem.placeholder ||
                    t('FORMS_MGMT.EDITOR.PREVIEW.SELECT_PLACEHOLDER')
                  }}
                </option>
              </select>

              <div
                v-else-if="fieldItem.type === 'radio'"
                class="csfp-choices csfp-choices--bare"
              >
                <label
                  v-for="option in normalizedOptions(fieldItem)"
                  :key="option.value"
                  class="csfp-choice csfp-choice--bare"
                >
                  <input type="radio" disabled />
                  <span>{{ option.label }}</span>
                </label>
              </div>

              <div
                v-else-if="fieldItem.type === 'checkbox'"
                class="csfp-choices csfp-choices--bare"
              >
                <label
                  v-for="option in normalizedOptions(fieldItem)"
                  :key="option.value"
                  class="csfp-choice csfp-choice--bare"
                >
                  <input type="checkbox" disabled />
                  <span>{{ option.label }}</span>
                </label>
              </div>

              <input
                v-else
                class="csfp-input csfp-input--bare"
                :type="nativeInputType(fieldItem)"
                :placeholder="barePlaceholder(fieldItem)"
                disabled
              />
            </div>

            <label v-else class="csfp-field csfp-consent csfp-consent--bare">
              <input type="checkbox" class="csfp-consent__input" disabled />
              <span
                class="csfp-consent__text"
                :data-required="fieldItem.required"
                >{{ fieldItem.label }}</span
              >
            </label>
          </template>

          <label
            v-if="isLastStep && showStandaloneConsent"
            class="csfp-consent csfp-consent--bare"
          >
            <input type="checkbox" class="csfp-consent__input" disabled />
            <span class="csfp-consent__text">{{
              consentText || t('FORMS_MGMT.EDITOR.PREVIEW.DEFAULT_CONSENT_TEXT')
            }}</span>
          </label>

          <div class="csfp-stepnav">
            <button
              v-if="!isFirstStep"
              type="button"
              class="csfp-stepnav__back"
              @click="goPrev"
            >
              <span class="i-lucide-chevron-left" aria-hidden="true" />
              {{ t('FORMS_MGMT.EDITOR.PREVIEW.STEP_BACK') }}
            </button>
            <button
              v-if="!isLastStep"
              type="button"
              class="csfp-stepnav__next"
              @click="goNext"
            >
              {{ t('FORMS_MGMT.EDITOR.PREVIEW.STEP_NEXT') }}
              <span class="i-lucide-check" aria-hidden="true" />
            </button>
            <button v-else type="button" class="csfp-submit" disabled>
              {{ t('FORMS_MGMT.EDITOR.PREVIEW.SUBMIT') }}
            </button>
          </div>
        </div>
      </div>

      <!-- Single-page layout: unchanged boxed card experience. -->
      <div v-else class="csfp-card">
        <div class="csfp-accent" />
        <img
          v-if="formHeaderImageUrl"
          :src="formHeaderImageUrl"
          alt=""
          class="csfp-cover"
        />

        <div class="csfp-card__body">
          <img v-if="logoUrl" :src="logoUrl" alt="" class="csfp-logo" />
          <h1 class="csfp-title">
            {{ name || t('FORMS_MGMT.EDITOR.PREVIEW.UNTITLED') }}
          </h1>

          <div
            v-for="fieldItem in visibleFields"
            :key="fieldItem.key"
            class="csfp-field"
          >
            <template v-if="fieldItem.type === 'consent'">
              <label class="csfp-consent">
                <input type="checkbox" class="csfp-consent__input" disabled />
                <span
                  class="csfp-consent__text"
                  :data-required="fieldItem.required"
                  >{{ fieldItem.label }}</span
                >
              </label>
            </template>

            <template v-else>
              <span class="csfp-label" :data-required="fieldItem.required">
                {{
                  fieldItem.label ||
                  t('FORMS_MGMT.EDITOR.PREVIEW.UNTITLED_FIELD')
                }}
              </span>

              <textarea
                v-if="fieldItem.type === 'textarea'"
                class="csfp-input csfp-textarea"
                rows="3"
                disabled
                :placeholder="fieldItem.placeholder"
              />

              <select
                v-else-if="fieldItem.type === 'select'"
                class="csfp-input csfp-select"
                disabled
              >
                <option>
                  {{
                    fieldItem.placeholder ||
                    t('FORMS_MGMT.EDITOR.PREVIEW.SELECT_PLACEHOLDER')
                  }}
                </option>
              </select>

              <div v-else-if="fieldItem.type === 'radio'" class="csfp-choices">
                <label
                  v-for="option in normalizedOptions(fieldItem)"
                  :key="option.value"
                  class="csfp-choice"
                >
                  <input type="radio" disabled />
                  <span>{{ option.label }}</span>
                </label>
              </div>

              <div
                v-else-if="fieldItem.type === 'checkbox'"
                class="csfp-choices"
              >
                <label
                  v-for="option in normalizedOptions(fieldItem)"
                  :key="option.value"
                  class="csfp-choice"
                >
                  <input type="checkbox" disabled />
                  <span>{{ option.label }}</span>
                </label>
              </div>

              <input
                v-else
                class="csfp-input"
                :type="nativeInputType(fieldItem)"
                :placeholder="fieldItem.placeholder"
                disabled
              />
            </template>
          </div>

          <label v-if="showStandaloneConsent" class="csfp-consent">
            <input type="checkbox" class="csfp-consent__input" disabled />
            <span class="csfp-consent__text">{{
              consentText || t('FORMS_MGMT.EDITOR.PREVIEW.DEFAULT_CONSENT_TEXT')
            }}</span>
          </label>

          <button type="button" class="csfp-submit" disabled>
            {{ t('FORMS_MGMT.EDITOR.PREVIEW.SUBMIT') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.csfp-viewport {
  flex: 1;
  min-height: 420px;
  max-height: 640px;
  overflow-y: auto;
  padding: 28px 18px;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  background: var(--csf-bg, #f5f4ef);
  font-family: var(
    --csf-font,
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    Roboto,
    sans-serif
  );
  color: var(--csf-text, #1b1b18);
}

.csfp-viewport * {
  box-sizing: border-box;
}

.csfp-empty {
  margin: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 32px 16px;
  text-align: center;
  color: rgba(27, 27, 24, 0.5);
  font-size: 13.5px;
}

.csfp-empty__icon {
  width: 28px;
  height: 28px;
}

.csfp-welcome-toggle {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  padding: 6px 12px;
  border: none;
  border-bottom: 1px solid var(--n-weak, rgba(0, 0, 0, 0.08));
  background: transparent;
  color: rgba(27, 27, 24, 0.55);
  font-size: 11.5px;
  font-weight: 500;
  cursor: pointer;
  text-align: left;
}

.csfp-welcome-toggle:hover {
  color: rgba(27, 27, 24, 0.85);
}

.csfp-welcome-toggle span {
  width: 13px;
  height: 13px;
}

/* Welcome/cover screen — mirrors App.vue's .csf-welcome at preview scale. */
.csfp-welcome {
  width: 100%;
  align-self: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 24px 12px;
}

.csfp-welcome__logo {
  display: block;
  height: 32px;
  width: auto;
  max-width: 160px;
  object-fit: contain;
  margin-bottom: 18px;
}

.csfp-welcome__cover {
  display: block;
  width: auto;
  max-width: 90%;
  max-height: calc(var(--csf-header-max-height, 150px) * 0.7);
  object-fit: contain;
  margin-bottom: 18px;
}

.csfp-welcome__title {
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.25;
  color: var(--csf-text, #1b1b18);
}

.csfp-welcome__desc {
  margin: 10px 0 0;
  max-width: 320px;
  font-size: 13px;
  line-height: 1.55;
  color: color-mix(in srgb, var(--csf-text, #1b1b18) 62%, transparent);
}

.csfp-welcome__button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 20px;
  min-height: 40px;
  padding: 0 20px;
  border: none;
  border-radius: var(--csf-radius, 16px);
  background: var(--csf-button, #146b57);
  color: var(--csf-button-text, #fff);
  font-size: 13.5px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
}

.csfp-welcome__button span {
  width: 15px;
  height: 15px;
}

.csfp-card {
  position: relative;
  width: 100%;
  max-width: 420px;
  background: var(--csf-card-bg, #fff);
  border-radius: var(--csf-radius-lg, 22px);
  box-shadow:
    0 1px 2px rgba(20, 20, 15, 0.04),
    0 14px 32px -14px rgba(20, 20, 15, 0.16);
  overflow: hidden;
}

.csfp-accent {
  height: 4px;
  width: 100%;
  background: linear-gradient(
    90deg,
    var(--csf-primary, #146b57),
    color-mix(
      in srgb,
      var(--csf-primary, #146b57) 55%,
      var(--csf-button, #146b57)
    )
  );
}

.csfp-cover {
  display: block;
  width: 100%;
  max-height: calc(var(--csf-header-max-height, 150px) * 0.7);
  object-fit: contain;
}

.csfp-card__body {
  padding: 22px 20px 24px;
}

.csfp-logo {
  display: block;
  height: 30px;
  width: auto;
  max-width: 160px;
  object-fit: contain;
  margin-bottom: 14px;
}

.csfp-title {
  margin: 0 0 16px;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.25;
  color: var(--csf-text, #1b1b18);
}

/* ---- Steps (Typeform-style) full-bleed layout — mirrors App.vue's
 * .csf-flow/.csf-topbar/.csf-qhead. Sits inside the bounded preview viewport
 * instead of the real 100vh page, so the "fixed" progress bar is sticky to
 * the top of the viewport's own scroll area rather than the browser
 * window. */
.csfp-flow {
  width: 100%;
  align-self: stretch;
  display: flex;
  flex-direction: column;
}

.csfp-progress {
  position: sticky;
  top: -28px;
  z-index: 1;
  display: block;
  height: 3px;
  margin: 0 0 18px;
  background: color-mix(in srgb, var(--csf-text, #1b1b18) 10%, transparent);
}

.csfp-progress__fill {
  display: block;
  height: 100%;
  background: var(--csf-primary, #146b57);
  transition: width 0.3s ease;
}

.csfp-flow__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.csfp-flow__logo {
  display: block;
  height: 24px;
  width: auto;
  max-width: 140px;
  object-fit: contain;
}

.csfp-flow__count {
  margin-left: auto;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: color-mix(in srgb, var(--csf-text, #1b1b18) 50%, transparent);
}

.csfp-flow__center {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 22px 0 4px;
}

.csfp-flow__cover {
  display: block;
  width: 100%;
  max-height: calc(var(--csf-header-max-height, 150px) * 0.7);
  object-fit: contain;
  border-radius: var(--csf-radius-lg, 22px);
  margin-bottom: 16px;
}

.csfp-qhead {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  flex-wrap: wrap;
}

.csfp-qnum {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding-top: 0.15em;
  font-size: 13px;
  font-weight: 700;
  color: var(--csf-primary, #146b57);
}

.csfp-qnum .i-lucide-corner-down-right {
  width: 13px;
  height: 13px;
}

.csfp-qbody {
  flex: 1 1 200px;
  min-width: 0;
}

.csfp-qtitle {
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.3;
  color: var(--csf-text, #1b1b18);
}

.csfp-qtitle[data-required='true']::after {
  content: ' *';
  color: var(--csf-primary, #146b57);
}

.csfp-qhelp {
  margin: 5px 0 0;
  font-size: 12.5px;
  line-height: 1.5;
  color: color-mix(in srgb, var(--csf-text, #1b1b18) 62%, transparent);
}

.csfp-qbody > .csfp-input--bare,
.csfp-qbody > .csfp-choices--bare {
  margin-top: 14px;
}

/* Grouped step sub-fields (fieldItem.group_with_previous) — mirrors
 * App.vue's .csf-qmore at preview scale. */
.csfp-qmore {
  margin-top: 20px;
}

.csfp-qmore__label {
  display: block;
  margin: 0 0 6px;
  font-size: 13.5px;
  font-weight: 600;
  color: var(--csf-text, #1b1b18);
}

.csfp-qmore__label[data-required='true']::after {
  content: ' *';
  color: var(--csf-primary, #146b57);
}

.csfp-qmore__help {
  margin: 4px 0 0;
  font-size: 12px;
  line-height: 1.5;
  color: color-mix(in srgb, var(--csf-text, #1b1b18) 62%, transparent);
}

.csfp-qmore > .csfp-input--bare,
.csfp-qmore > .csfp-choices--bare {
  margin-top: 8px;
}

.csfp-stepnav {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 18px;
}

.csfp-stepnav__back {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 4px 2px;
  border: none;
  background: transparent;
  color: color-mix(in srgb, var(--csf-text, #1b1b18) 55%, transparent);
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
}

.csfp-stepnav__next {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-height: 38px;
  padding: 0 16px;
  border: none;
  border-radius: var(--csf-radius, 16px);
  background: var(--csf-button, #146b57);
  color: var(--csf-button-text, #fff);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
}

.csfp-field {
  margin-bottom: 14px;
}

.csfp-label {
  display: block;
  margin-bottom: 5px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.01em;
  color: var(--csf-text);
}

.csfp-label[data-required='true']::after,
.csfp-consent__text[data-required='true']::after {
  content: ' *';
  color: #b3261e;
}

.csfp-input {
  width: 100%;
  min-height: 40px;
  padding: 9px 12px;
  font-size: 13.5px;
  font-family: var(--csf-font);
  color: var(--csf-text);
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.14);
  border-radius: var(--csf-radius);
  cursor: default;
}

.csfp-input::placeholder {
  color: rgba(0, 0, 0, 0.35);
}

.csfp-textarea {
  min-height: 72px;
  resize: none;
}

.csfp-select {
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='9' viewBox='0 0 14 9'%3E%3Cpath fill='none' stroke='%23555' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round' d='M1 1l6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  padding-right: 32px;
}

.csfp-choices {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.csfp-choice {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  font-size: 13px;
  color: var(--csf-text);
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: calc(var(--csf-radius) * 0.7);
}

.csfp-choice input {
  width: 15px;
  height: 15px;
  accent-color: var(--csf-primary);
}

.csfp-consent {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  margin-bottom: 14px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: calc(var(--csf-radius) * 0.7);
  font-size: 12.5px;
  line-height: 1.5;
}

.csfp-consent__input {
  margin-top: 2px;
  width: 15px;
  height: 15px;
  flex-shrink: 0;
  accent-color: var(--csf-primary);
}

.csfp-consent__text {
  color: var(--csf-text);
}

.csfp-submit {
  width: 100%;
  margin-top: 6px;
  min-height: 42px;
  border: none;
  border-radius: var(--csf-radius, 16px);
  background: var(--csf-button, #146b57);
  color: var(--csf-button-text, #fff);
  font-size: 13.5px;
  font-weight: 600;
  cursor: default;
}

/* Inside the steps nav row, the submit button replaces .csfp-stepnav__next
 * in place — same compact pill sizing, not the full-width single-layout
 * button above. */
.csfp-stepnav .csfp-submit {
  width: auto;
  flex: 1;
  margin-top: 0;
  min-height: 38px;
  font-size: 13px;
}

/* Bare variant — steps layout. No boxes, just an underline, mirroring
 * app/javascript/forms/components/FormField.vue's .csf-field--bare. */
.csfp-input--bare {
  padding: 8px 2px 10px;
  font-size: 15px;
  background: transparent;
  border: none;
  border-bottom: 2px solid
    color-mix(in srgb, var(--csf-text, #1b1b18) 22%, transparent);
  border-radius: 0;
}

.csfp-input--bare::placeholder {
  color: color-mix(in srgb, var(--csf-text, #1b1b18) 32%, transparent);
}

.csfp-choices--bare {
  gap: 0;
}

.csfp-choice--bare {
  padding: 9px 2px;
  background: transparent;
  border: none;
  border-bottom: 1px solid
    color-mix(in srgb, var(--csf-text, #1b1b18) 14%, transparent);
  border-radius: 0;
}

.csfp-choice--bare:last-child {
  border-bottom: none;
}

.csfp-consent--bare {
  background: transparent;
  border: none;
  border-bottom: 1px solid
    color-mix(in srgb, var(--csf-text, #1b1b18) 14%, transparent);
  border-radius: 0;
  padding: 10px 2px;
  margin-bottom: 0;
  margin-top: 4px;
}
</style>
